import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as d3 from 'd3';
import {
  Cpu,
  Play,
  Loader2,
  Download,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  Sparkles,
  Shield,
  Activity
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUMDMODigitalTwin = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [simulation, setSimulation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  
  const gaugeRef = useRef(null);
  const ganttRef = useRef(null);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  useEffect(() => {
    if (simulation) {
      if (activeTab === 'overview') {
        renderReadinessGauge();
      }
      if (activeTab === 'timeline') {
        renderGanttChart();
      }
    }
  }, [simulation, activeTab]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/sum-digital-twin/latest`);
      setSimulation(response.data.simulation);
    } catch (error) {
      console.log('No SUM simulation yet');
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async () => {
    setSimulating(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/sum-digital-twin/simulate`, {});
      setSimulation(response.data);
    } catch (error) {
      console.error('Error:', error);
      alert('Error running SUM simulation. Ensure Phase 2 modules have been run first.');
    } finally {
      setSimulating(false);
    }
  };

  const renderReadinessGauge = () => {
    if (!gaugeRef.current || !simulation) return;

    const svg = d3.select(gaugeRef.current);
    svg.selectAll('*').remove();

    const width = 300;
    const height = 200;
    const radius = 90;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${width / 2}, ${height - 20})`);

    // Background
    const backgroundArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2);

    g.append('path')
      .attr('d', backgroundArc)
      .attr('fill', '#E5E7EB');

    // Color zones (RED < 60, AMBER 60-80, GREEN >= 80)
    const redArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(-Math.PI / 2 + (60 / 100) * Math.PI);

    const amberArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2 + (60 / 100) * Math.PI)
      .endAngle(-Math.PI / 2 + (80 / 100) * Math.PI);

    const greenArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2 + (80 / 100) * Math.PI)
      .endAngle(Math.PI / 2);

    g.append('path').attr('d', redArc).attr('fill', '#EF4444').attr('opacity', 0.6);
    g.append('path').attr('d', amberArc).attr('fill', '#F59E0B').attr('opacity', 0.6);
    g.append('path').attr('d', greenArc).attr('fill', '#10B981').attr('opacity', 0.6);

    // Current value
    const score = simulation.sum_readiness.sum_readiness_score;
    const valueAngle = -Math.PI / 2 + (score / 100) * Math.PI;

    const valueArc = d3.arc()
      .innerRadius(radius - 20)
      .outerRadius(radius)
      .startAngle(-Math.PI / 2)
      .endAngle(valueAngle);

    const color = score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : '#EF4444';
    g.append('path')
      .attr('d', valueArc)
      .attr('fill', color);

    // Center text
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', -30)
      .style('font-size', '36px')
      .style('font-weight', 'bold')
      .style('fill', color)
      .text(score);

    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', 0)
      .style('font-size', '14px')
      .style('fill', '#6B7280')
      .text('SUM Readiness');

    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('y', 20)
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', color)
      .text(simulation.sum_readiness.rag_status);
  };

  const renderGanttChart = () => {
    if (!ganttRef.current || !simulation) return;

    const svg = d3.select(ganttRef.current);
    svg.selectAll('*').remove();

    const data = simulation.phase_predictions;
    const width = 900;
    const height = 400;
    const margin = { top: 30, right: 100, bottom: 60, left: 150 };

    svg.attr('width', width).attr('height', height);

    // Calculate cumulative start times
    let cumulative = 0;
    const ganttData = data.map(phase => {
      const start = cumulative;
      cumulative += phase.predicted_duration_hours;
      return {
        ...phase,
        start_hours: start,
        end_hours: cumulative
      };
    });

    const y = d3.scaleBand()
      .domain(data.map(d => d.phase_name))
      .range([margin.top, height - margin.bottom])
      .padding(0.2);

    const x = d3.scaleLinear()
      .domain([0, cumulative])
      .range([margin.left, width - margin.right]);

    // Bars
    svg.selectAll('rect')
      .data(ganttData)
      .join('rect')
      .attr('x', d => x(d.start_hours))
      .attr('y', d => y(d.phase_name))
      .attr('width', d => x(d.end_hours) - x(d.start_hours))
      .attr('height', y.bandwidth())
      .attr('fill', d => d.downtime_required ? '#EF4444' : '#3B82F6')
      .attr('rx', 4);

    // Duration labels
    svg.selectAll('text.duration')
      .data(ganttData)
      .join('text')
      .attr('class', 'duration')
      .attr('x', d => x(d.start_hours) + (x(d.end_hours) - x(d.start_hours)) / 2)
      .attr('y', d => y(d.phase_name) + y.bandwidth() / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', 'white')
      .text(d => `${d.predicted_duration_hours.toFixed(1)}h`);

    // Y axis
    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y))
      .selectAll('text')
      .style('font-size', '12px')
      .style('font-weight', 'bold');

    // X axis
    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).ticks(10).tickFormat(d => `${d}h`));

    // Legend
    const legend = svg.append('g')
      .attr('transform', `translate(${width - margin.right + 10}, ${margin.top})`);

    legend.append('rect')
      .attr('width', 15)
      .attr('height', 15)
      .attr('fill', '#EF4444');

    legend.append('text')
      .attr('x', 20)
      .attr('y', 12)
      .style('font-size', '11px')
      .text('Downtime');

    legend.append('rect')
      .attr('y', 20)
      .attr('width', 15)
      .attr('height', 15)
      .attr('fill', '#3B82F6');

    legend.append('text')
      .attr('x', 20)
      .attr('y', 32)
      .style('font-size', '11px')
      .text('Uptime');
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-12 h-12 text-cyan-600 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">SUM/DMO Digital Twin</h2>
            <p className="text-gray-600">Virtual simulation to predict SUM outcomes and downtime</p>
            {simulation && simulation.sap_system_analyzed && simulation.sap_system_analyzed.name && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <span className="text-gray-500">Analyzing System:</span>
                <span className="font-medium text-cyan-700">{simulation.sap_system_analyzed.name}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${
                  simulation.data_source === 'Real SAP System' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {simulation.data_source || 'Mock Data'}
                </span>
              </div>
            )}
          </div>
          <button 
            onClick={runSimulation}
            disabled={simulating}
            className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
          >
            {simulating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Simulating...</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                <span>Run Simulation</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Empty State */}
      {!simulation && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Cpu className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No SUM Simulation</h3>
          <p className="text-gray-600 mb-6">Run virtual simulation to predict SUM/DMO outcomes</p>
          <button 
            onClick={runSimulation}
            className="bg-cyan-600 text-white px-8 py-3 rounded-xl hover:bg-cyan-700 inline-flex items-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>Run Simulation</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      {simulation && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-6 text-white shadow-lg">
              <Clock className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{simulation.summary.total_runtime_hours}h</div>
              <div className="text-cyan-100 text-sm">Total Runtime</div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
              <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{simulation.summary.total_downtime_hours}h</div>
              <div className="text-red-100 text-sm">Predicted Downtime</div>
            </div>
            <div className={`rounded-xl p-6 shadow-lg border-2 ${
              simulation.sum_readiness.rag_status === 'GREEN' ? 'bg-green-100 border-green-500' :
              simulation.sum_readiness.rag_status === 'AMBER' ? 'bg-yellow-100 border-yellow-500' :
              'bg-red-100 border-red-500'
            }`}>
              <Shield className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{simulation.summary.readiness_score}</div>
              <div className="text-sm opacity-80">Readiness Score</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <Activity className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{simulation.summary.total_blockers}</div>
              <div className="text-orange-100 text-sm">Predicted Blockers</div>
            </div>
          </div>

          {/* RAG Status */}
          <div className={`border-2 rounded-xl p-6 ${
            simulation.sum_readiness.rag_status === 'GREEN' ? 'bg-green-50 border-green-500' :
            simulation.sum_readiness.rag_status === 'AMBER' ? 'bg-yellow-50 border-yellow-500' :
            'bg-red-50 border-red-500'
          }`}>
            <h3 className="text-xl font-bold mb-2">SUM Readiness Status: {simulation.sum_readiness.rag_status}</h3>
            <p className="text-gray-700">{simulation.sum_readiness.rag_reasoning}</p>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: TrendingUp },
                { id: 'timeline', label: 'SUM Timeline', icon: Clock },
                { id: 'blockers', label: 'Blockers', icon: AlertTriangle },
                { id: 'downtime', label: 'Downtime', icon: Clock },
                { id: 'narrative', label: 'AI Insights', icon: Sparkles }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Readiness Gauge */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4 text-center">SUM Readiness Score</h3>
                    <div className="flex justify-center">
                      <svg ref={gaugeRef}></svg>
                    </div>
                  </div>

                  {/* Component Scores */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Readiness Component Scores</h4>
                    <div className="space-y-3">
                      {Object.entries(simulation.sum_readiness.component_scores).map(([component, score]) => (
                        <div key={component} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700 capitalize">
                              {component.replace(/_/g, ' ')}
                            </span>
                            <span className="text-sm font-bold text-gray-900">{score}/100</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all ${
                                score >= 80 ? 'bg-green-600' : score >= 60 ? 'bg-yellow-500' : 'bg-red-600'
                              }`}
                              style={{ width: `${score}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Timeline Tab */}
              {activeTab === 'timeline' && (
                <div className="space-y-6">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Predicted SUM Timeline (Gantt)</h4>
                    <div className="overflow-x-auto">
                      <svg ref={ganttRef}></svg>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Phase Details</h4>
                    <div className="space-y-3">
                      {simulation.phase_predictions.map((phase) => (
                        <div key={phase.phase_id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-1">
                                <h5 className="font-bold text-gray-900">{phase.phase_name}</h5>
                                {phase.downtime_required && (
                                  <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded-full">
                                    DOWNTIME
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{phase.description}</p>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-2xl font-bold text-cyan-600">{phase.predicted_duration_hours}h</div>
                              <div className="text-xs text-gray-500">Duration</div>
                            </div>
                          </div>

                          {phase.predicted_blockers.length > 0 && (
                            <div className="mt-3 bg-orange-50 border border-orange-200 rounded p-3">
                              <p className="text-xs font-semibold text-gray-900 mb-2">Predicted Blockers:</p>
                              {phase.predicted_blockers.map((blocker, idx) => (
                                <div key={idx} className="text-xs text-gray-700 mb-1">
                                  <span className="font-semibold">{blocker.blocker_type}:</span> {blocker.description}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Blockers Tab */}
              {activeTab === 'blockers' && (
                <div className="space-y-4">
                  {simulation.blocker_predictions.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
                      <p>No critical blockers predicted - System ready for SUM!</p>
                    </div>
                  )}

                  {simulation.blocker_predictions.map((blocker) => (
                    <div key={blocker.blocker_id} className="bg-white border-2 border-orange-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              blocker.severity === 'Blocker' ? 'bg-red-600 text-white' :
                              blocker.severity === 'High' ? 'bg-orange-500 text-white' :
                              'bg-yellow-500 text-white'
                            }`}>
                              {blocker.severity}
                            </span>
                            <h4 className="text-lg font-bold text-gray-900">{blocker.category}</h4>
                          </div>
                          <p className="text-sm text-gray-700 mb-2">{blocker.description}</p>
                          <p className="text-xs text-gray-500">Phase Impact: {blocker.phase_impact}</p>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-2xl font-bold text-orange-600">
                            {(blocker.likelihood_score * 100).toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-500">Likelihood</div>
                        </div>
                      </div>

                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-1">Recommended Fix:</p>
                        <p className="text-sm text-gray-700">{blocker.recommended_fix}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Downtime Tab */}
              {activeTab === 'downtime' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Downtime Estimation</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Total Downtime (Baseline)</p>
                        <p className="text-4xl font-bold text-red-600">{simulation.downtime_estimate.total_downtime_hours}h</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Optimized Downtime</p>
                        <p className="text-4xl font-bold text-green-600">{simulation.downtime_estimate.optimized_downtime_hours}h</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {simulation.downtime_estimate.optimization_applied.savings_pct}% reduction
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Downtime Breakdown by Phase</h4>
                    <div className="space-y-3">
                      {simulation.downtime_estimate.downtime_breakdown.map((phase, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <span className="text-sm font-semibold text-gray-900">{phase.phase}</span>
                          <span className="text-lg font-bold text-red-600">{phase.hours}h</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Optimization Techniques Applied</h4>
                    <ul className="space-y-2">
                      {simulation.downtime_estimate.optimization_applied.uptime_preprocessing && (
                        <li className="flex items-center space-x-2 text-sm text-gray-700">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                          <span>Uptime Preprocessing (shadow instance work done before downtime)</span>
                        </li>
                      )}
                      <li className="flex items-center space-x-2 text-sm text-gray-700">
                        {simulation.downtime_estimate.optimization_applied.nzdm_enabled ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-gray-400" />
                        )}
                        <span>nZDM (near-Zero Downtime Maintenance)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* AI Narrative Tab */}
              {activeTab === 'narrative' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold flex items-center space-x-2">
                        <Sparkles className="w-6 h-6 text-cyan-600" />
                        <span>SUM Cutover Brief</span>
                      </h3>
                      {simulation.ai_narratives.generated_by.includes('GPT') && (
                        <span className="px-3 py-1 bg-cyan-600 text-white text-xs font-bold rounded-full">
                          {simulation.ai_narratives.generated_by}
                        </span>
                      )}
                    </div>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">{simulation.ai_narratives.executive_summary}</p>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Recommended Pre-SUM Actions</h4>
                    <ul className="space-y-3">
                      {simulation.blocker_predictions.map((blocker, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{blocker.recommended_fix}</p>
                            <p className="text-sm text-gray-600 mt-1">Impact: {blocker.category} ({blocker.phase_impact} phase)</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SUMDMODigitalTwin;
